// Copyright (C) 2021 Bystronic Laser AG
import { AlertOccurrence } from '@/models/alertOccurrence';
import {
  AlertKPI,
  AlertOperator,
  AlertSeverity,
  CareAlertMetric,
  WccAlertMetric,
} from '@/models/enums/AlertsEnums';
import i18n from '@/i18n';
import { translateDateGrouping } from '@/utils/dates';
import { careAlertsService } from '@/services/careAlerts.service';
import {
  formatWithUnit as formatNumberWithUnit,
  translateUnit,
} from './number';
import { isEmpty, isNil, valueOrDefault } from './misc';
import { matchIgnoringCase } from '@/utils/table';
import { AlertWorkflow, AlertWorkflowStatus } from '@/models/alertWorkflow';
import {
  StandardAlertListItem,
  StandardAlertStatus,
} from '@/models/standardAlert';
import {
  CareAlertListItem,
  CareAlertStatus,
  PresetAlertListItem,
} from '@/models/careAlert';
import { CareAlertType } from '@/models/enums/CareAlertType';
import { WccAlertNotification } from '@/models/wccAlertNotification';
import { Logger } from './logger';
import { EquipmentEServiceData } from '@/views/careConsole/commons/model';

/**
 * Returns true if `searchText` matches the care alert type.
 *
 * @param alert Care alert to check if it matches the type.
 * @param searchText Text to match against the care alert type.
 */
export function matchesType(
  alert: CareAlertListItem,
  searchText: string,
): boolean {
  return matchIgnoringCase(translateType(alert.type), searchText);
}

export function matchesMetric(
  alert: StandardAlertListItem | CareAlertListItem | AlertOccurrence,
  searchText: string,
): boolean {
  return matchIgnoringCase(translateKpi(alert.metric), searchText);
}

export function matchesOperator(
  alert: StandardAlertListItem | CareAlertListItem | AlertOccurrence,
  searchText: string,
): boolean {
  return matchIgnoringCase(translateOperator(alert.operator), searchText);
}

export function matchesDateGrouping(
  alert: CareAlertListItem | AlertOccurrence,
  searchText: string,
): boolean {
  return matchIgnoringCase(
    translateDateGrouping(alert.dateGrouping!),
    searchText,
  );
}

export function matchesThreshold(
  alert: StandardAlertListItem | CareAlertListItem | AlertOccurrence,
  searchText: string,
): boolean {
  const unit = careAlertsService.store.getMetricUnit(alert.metric);
  return matchIgnoringCase(
    `${alert.threshold} ${translateUnit(unit, alert.threshold)}`,
    searchText,
  );
}

export function matchesVariable(
  alert: CareAlertListItem | AlertOccurrence,
  searchText: string,
): boolean {
  return matchIgnoringCase(
    translateVariable(alert.variable, alert.metric as CareAlertMetric),
    searchText,
  );
}

export function matchesEventSource(
  alert: CareAlertListItem | AlertOccurrence,
  searchText: string,
): boolean {
  return (
    alert.eventSources?.some((source) =>
      matchIgnoringCase(source, searchText),
    ) ?? false
  );
}

export function matchesEventCode(
  alert: CareAlertListItem | AlertOccurrence,
  searchText: string,
): boolean {
  return (
    alert.eventCodes?.some((code) => matchIgnoringCase(code, searchText)) ??
    false
  );
}

/**
 * Returns true if `searchText` matches the care alert severity.
 *
 * @param alert Care alert to check if it matches the severity.
 * @param searchText Text to match against the care alert severity.
 */
export function matchesSeverity(
  alert: CareAlertListItem,
  searchText: string,
): boolean {
  return matchIgnoringCase(translateSeverity(alert.severity), searchText);
}

/**
 * Returns true if `searchText` matches the care alert status.
 *
 * @param alert Care alert to check if it matches the status.
 * @param searchText Text to match against the care alert status.
 */
export function matchesStatus(
  alert: CareAlertListItem,
  searchText: string,
): boolean {
  return matchIgnoringCase(translateStatus(alert.status), searchText);
}

export function matchesStandardAlertStatus(
  standardAlert: StandardAlertListItem,
  searchText: string,
): boolean {
  return matchIgnoringCase(
    translateStandardAlertStatus(standardAlert.status),
    searchText,
  );
}

export function modelMatcher(
  equipmentItem: EquipmentEServiceData,
  searchText: string,
): boolean {
  const displayedModel = valueOrDefault(
    equipmentItem.model,
    i18n.t('e_service_center.equipment_list.unknown_model').toString(),
  );
  return matchIgnoringCase(displayedModel, searchText);
}

export function translateKpi(kpi: WccAlertMetric | CareAlertMetric): string {
  return i18n.t(`alert.metric.${kpi}`).toString();
}

export function translateOperator(operator: AlertOperator): string {
  return i18n.t(`alert_operators.${AlertOperator[operator]}`).toString();
}

export function translateType(type: CareAlertType): string {
  return i18n.t(`e_service_center.care_alert_type.${type}`).toString();
}

export function translateStandardAlertStatus(
  status: StandardAlertStatus,
): string {
  return i18n
    .t(`e_service_center.standard_alert_status.${StandardAlertStatus[status]}`)
    .toString();
}

export function translateStatus(status: CareAlertStatus): string {
  return i18n
    .t(`e_service_center.alert_status.${CareAlertStatus[status]}`)
    .toString();
}

export function translateSeverity(severity: AlertSeverity): string {
  return i18n.t(`e_service_center.alert_severity.${severity}`).toString();
}

export function translateVariable(
  variable: string | undefined,
  metric: CareAlertMetric,
): string {
  if (isNil(variable)) {
    return '';
  }
  return i18n
    .t(`wizards.create_care_alert.variables.${metric}.${variable}`)
    .toString();
}

export function translateWorkflowStatus(
  workflowStatus: AlertWorkflowStatus,
): string {
  return i18n.t(`alert.workflow_status.${workflowStatus}`).toString();
}

export function formatWithUnit(value: number, metric: CareAlertMetric): string {
  const unit = careAlertsService.store.getMetricUnit(metric);
  return formatNumberWithUnit(value, unit);
}

export function alertMetricSorter(
  a: AlertWorkflow,
  b: AlertWorkflow,
  isAscending: boolean,
): number {
  const aText = translateKpi(a.alertOccurrence.metric);
  const bText = translateKpi(b.alertOccurrence.metric);
  return isAscending ? aText.localeCompare(bText) : bText.localeCompare(aText);
}

export function isVariableRequiredBy(kpi: AlertKPI<any> | null): boolean {
  return !isEmpty(kpi?.variableValues);
}

export function areSourcesRequiredBy(kpi: AlertKPI<any> | null): boolean {
  if (isNil(kpi)) {
    return false;
  }
  return [
    CareAlertMetric.ErrorCountBySource,
    CareAlertMetric.WarningCountBySource,
  ].includes(kpi.kpi);
}

export function areEventCodesRequiredBy(kpi: AlertKPI<any> | null): boolean {
  if (isNil(kpi)) {
    return false;
  }
  return [CareAlertMetric.ErrorCount, CareAlertMetric.WarningCount].includes(
    kpi.kpi,
  );
}

export function isPresetAlert(
  alert: CareAlertListItem,
): alert is PresetAlertListItem {
  return alert.type === CareAlertType.Preset;
}

export function getWccNotificationMessage(notification: WccAlertNotification) {
  const params = {
    metric: i18n.t(`alert.metric.${notification.metric}`),
    thresholdValue: notification.thresholdValue,
    device: notification.deviceName ?? i18n.t('alert.all_devices'),
    currentValue: i18n.n(notification.currentValue, {
      maximumFractionDigits: 2,
    }),
  };

  switch (notification.operator) {
    case AlertOperator.GreaterThan: {
      return i18n.t('alert.message_higher', params);
    }
    case AlertOperator.LessThan: {
      return i18n.t('alert.message_lower', params);
    }
    case AlertOperator.Equal: {
      return i18n.t('alert.message_equal', params);
    }
    default:
      Logger.error(`Invalid operator for notification ${notification}`);
  }
}
